import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseEdgeSwipeBackOptions {
  enabled?: boolean;
  edgeWidth?: number;
  threshold?: number;
}

/**
 * Hook to enable edge-swipe navigation (swipe from left edge to go back)
 * Similar to iOS Safari's back gesture
 */
export function useEdgeSwipeBack(options: UseEdgeSwipeBackOptions = {}) {
  const { enabled = true, edgeWidth = 30, threshold = 100 } = options;
  const navigate = useNavigate();
  
  const startX = useRef<number>(0);
  const startY = useRef<number>(0);
  const currentX = useRef<number>(0);
  const isEdgeSwipe = useRef<boolean>(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const createOverlay = useCallback(() => {
    if (overlayRef.current) return;
    
    const overlay = document.createElement('div');
    overlay.id = 'edge-swipe-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, rgba(0,0,0,0.1), transparent 50%);
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.1s ease-out;
    `;
    document.body.appendChild(overlay);
    overlayRef.current = overlay;
  }, []);

  const removeOverlay = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.remove();
      overlayRef.current = null;
    }
  }, []);

  const updateOverlay = useCallback((progress: number) => {
    if (overlayRef.current) {
      overlayRef.current.style.opacity = String(Math.min(progress / threshold, 0.5));
    }
  }, [threshold]);

  useEffect(() => {
    if (!enabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      
      // Only trigger if starting from the left edge
      if (touch.clientX <= edgeWidth) {
        isEdgeSwipe.current = true;
        createOverlay();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isEdgeSwipe.current) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - startX.current;
      const deltaY = touch.clientY - startY.current;
      
      // Cancel if vertical scroll is more dominant
      if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
        isEdgeSwipe.current = false;
        removeOverlay();
        return;
      }
      
      // Only respond to rightward swipes
      if (deltaX > 0) {
        currentX.current = deltaX;
        updateOverlay(deltaX);
        
        // Prevent default to stop page scrolling
        if (deltaX > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isEdgeSwipe.current) return;
      
      const deltaX = currentX.current;
      
      // Navigate back if threshold is met
      if (deltaX >= threshold) {
        navigate(-1);
      }
      
      // Reset state
      isEdgeSwipe.current = false;
      currentX.current = 0;
      removeOverlay();
    };

    const handleTouchCancel = () => {
      isEdgeSwipe.current = false;
      currentX.current = 0;
      removeOverlay();
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
      removeOverlay();
    };
  }, [enabled, edgeWidth, threshold, navigate, createOverlay, removeOverlay, updateOverlay]);
}
